import VectorSource from 'ol/source/Vector';
import { type Coords } from '#shared/lib/coords';
import type { Map } from './map';
import VectorLayer from 'ol/layer/Vector';
import Style from 'ol/style/Style';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromCoords, toCoords, areCoordsEqual } from './utils';
import type { Icon } from 'ol/style';
import { renderMarkerWithText } from './markerRenderer';
import { transformExtent } from 'ol/proj';

export type MapMarkerRenderer = (marker: MapMarker) => Icon;

export type MapMarker = {
  coords: Coords;
  properties?: Record<string, unknown>;
};

export type MapMarkerLayerOptions = {
  renderer?: MapMarkerRenderer;
  onMarkerClick?: (marker: MapMarker) => void;
};

export type MapMarkerInternal = {
  marker: MapMarker;
  icon: Icon;
};

export class MapMarkerLayer {
  static #layerClassName = 'markers';

  readonly #source: VectorSource;
  #markers: MapMarkerInternal[] = [];
  #renderer: MapMarkerRenderer;

  constructor({ olMap }: Map, options: MapMarkerLayerOptions = {}) {
    const source = new VectorSource();
    this.#renderer = options.renderer ?? (() => renderMarkerWithText('o'));
    this.#source = source;
    const markerLayer = new VectorLayer({
      className: MapMarkerLayer.#layerClassName,
      zIndex: 10,
      source,
    });
    olMap.addLayer(markerLayer);

    olMap.on('pointermove', (e) => {
      const pixel = olMap.getEventPixel(e.originalEvent);
      const hit = olMap.hasFeatureAtPixel(pixel, {
        layerFilter: (layer) =>
          layer.getClassName() === MapMarkerLayer.#layerClassName,
      });
      olMap.getViewport().style.cursor = hit ? 'pointer' : '';
    });
    olMap.on('singleclick', (e) => {
      const pixel = olMap.getEventPixel(e.originalEvent);
      const features = olMap.getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer.getClassName() === 'markers',
      });
      if (features.length < 1) return;
      const [feature] = features as Feature<Point>[];
      options.onMarkerClick?.({
        coords: toCoords(feature.getGeometry()!.getCoordinates()),
        properties: feature.getProperties(),
      });
    });
  }

  setMarkers(markers: MapMarker[]) {
    this.#markers = markers.map<MapMarkerInternal>((marker) => {
      const oldMarker = this.#markers.find((m) =>
        areCoordsEqual(m.marker.coords, marker.coords)
      );
      return { marker, icon: oldMarker?.icon ?? this.#renderer(marker) };
    });
    const features = this.#markers.map((marker) => {
      const feature = new Feature({
        geometry: new Point(fromCoords(marker.marker.coords)),
      });
      feature.setStyle(new Style({ image: marker.icon }));
      feature.setProperties(marker.marker.properties ?? {});
      return feature;
    });
    this.#source.clear(true);
    this.#source.addFeatures(features);
  }

  getExtent() {
    const extent = this.#source.getExtent();
    return transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
  }
}
